// useIssueForm.jsx - UPDATED with reviewer and shaId state/handling
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ensureAttachmentsArray = (attachments) => {
  if (Array.isArray(attachments)) {
    return attachments;
  } else if (attachments === null || attachments === undefined) {
    return [];
  } else if (typeof attachments === 'string') {
    try {
      const parsed = JSON.parse(attachments);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  } else {
    return [];
  }
};

const normalizeIssueAttachments = (issue) => {
  if (!issue) return issue;
  return {
    ...issue,
    attachments: ensureAttachmentsArray(issue.attachments)
  };
};

const createApiClient = () => {
  const baseURL = `${API_URL}`;
  
  return {
    async getProjects() {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
    
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        const response = await fetch(`${baseURL}/api/projects`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            },
            credentials: 'include' 
          }
        );
        if (!response.ok) throw new Error('Failed to fetch projects');
        const data = await response.json();
        return data.data || [];
      } catch (error) {
        //console.error('Error fetching projects:', error);
        throw error;
      }
    },
    
    async getProjectHierarchy(projectId) {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
    
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        const response = await fetch(`${baseURL}/api/projects/${projectId}/hierarchy`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        if (!response.ok) {
          console.warn(`Project hierarchy endpoint returned ${response.status}, continuing without hierarchy data`);
          return null;
        }
        const data = await response.json();
        return data.data || null;
      } catch (error) {
        console.warn('Project hierarchy not available:', error);
        return null;
      }
    },
    
    async getProjectDetails(projectId) {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
    
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        const response = await fetch(`${baseURL}/api/projects/projectname/${projectId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        if (!response.ok) throw new Error('Failed to fetch project details');
        const data = await response.json();
        
        if (!data || !data.data) {
          console.warn('Unexpected project details response format:', data);
          return null;
        }
        
        return data.data;
      } catch (error) {
        //console.error('Error fetching project details:', error);
        throw error;
      }
    },
    
    async fetchHierarchyData(params) {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
    
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        const searchParams = new URLSearchParams();
        
        const paramMapping = {
          'projectId': 'project_id',
          'project_id': 'project_id',
          'epicId': 'epic_id', 
          'epic_id': 'epic_id',
          'storyId': 'story_id',
          'story_id': 'story_id', 
          'taskId': 'task_id',
          'task_id': 'task_id',
          'type': 'type',
          'issueType': 'issue_type',
          'issue_type': 'issue_type'
        };
        
        Object.keys(params).forEach(key => {
          if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
            const backendKey = paramMapping[key] || key;
            searchParams.append(backendKey, params[key]);
          }
        });
        
        const url = `${baseURL}/api/issues/hierarchy?${searchParams.toString()}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        });
    
        if (!response.ok) {
          const errorText = await response.text();
          //console.error(`API Client - HTTP ${response.status} error:`, errorText);
          
          if (response.status === 400) {
            throw new Error(`Bad Request: Invalid parameters. Check if the selected parent item exists and is valid.`);
          } else if (response.status === 404) {
            throw new Error(`Not Found: The requested resource doesn't exist.`);
          } else if (response.status === 500) {
            throw new Error(`Server Error: Internal server error occurred.`);
          } else {
            throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
          }
        }
    
        const data = await response.json();
        return data;
      } catch (error) {
        //console.error('API Client - Error fetching hierarchy data:', error);
        throw error;
      }
    },
    
    async getEpics(projectId) {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
    
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        
        try {
          const data = await this.fetchHierarchyData({ 
            project_id: projectId,
            type: 'epic'
          });
          
          let epicsData = [];
          if (data.success && data.data) {
            epicsData = data.data.epics || data.data || [];
          } else if (data.epics) {
            epicsData = data.epics;
          } else if (Array.isArray(data.data)) {
            epicsData = data.data;
          } else if (Array.isArray(data)) {
            epicsData = data;
          }
          
          // FIXED: Normalize attachments for each epic
          epicsData = epicsData.map(normalizeIssueAttachments);
          
          return epicsData;
        } catch (hierarchyError) {
          console.warn('Hierarchy endpoint failed, trying direct epic endpoint:', hierarchyError.message);
          
          const response = await fetch(`${baseURL}/api/epics?project_id=${projectId}`,{
            headers:{
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            }
          });
          if (!response.ok) {
            throw new Error(`Failed to fetch epics: ${response.status}`);
          }
          
          const data = await response.json();
          let epicsData = data.data || data.epics || data || [];
          // FIXED: Normalize attachments for each epic in fallback
          epicsData = Array.isArray(epicsData) ? epicsData.map(normalizeIssueAttachments) : [];
          return epicsData;
        }
      } catch (error) {
        //console.error('API Client - Error fetching epics:', error);
        throw error;
      }
    },

    async getStories(epicId) {
      try {
        
        try {
          const data = await this.fetchHierarchyData({ 
            epic_id: epicId,
            type: 'story'
          });
          
          let storiesData = [];
          if (data && data.success && data.data) {
            storiesData = data.data.stories || data.data || [];
          } else if (data && data.stories) {
            storiesData = data.stories;
          } else if (data && Array.isArray(data.data)) {
            storiesData = data.data;
          } else if (Array.isArray(data)) {
            storiesData = data;
          }
          
          // FIXED: Normalize attachments for each story
          storiesData = storiesData.map(normalizeIssueAttachments);
          
          return storiesData;
        } catch (hierarchyError) {
          console.warn('Hierarchy endpoint failed, trying direct story endpoint:', hierarchyError.message);
          const token = localStorage.getItem("token");
          if (!token) {
            alert("No authentication token found. Please log in.");
            return;
          }
    
          const payload = JSON.parse(atob(token.split('.')[1]));
          const company_id = payload.company_id;
          
          const response = await fetch(`${baseURL}/api/stories?epic_id=${epicId}`,{
            headers:{
              Authorization: `Bearer ${token}`,
            }
          });
          if (!response.ok) {
            throw new Error(`Failed to fetch stories: ${response.status}`);
          }
          
          const data = await response.json();
          let storiesData = data.data || data.stories || data || [];
          // FIXED: Normalize attachments for each story in fallback
          storiesData = Array.isArray(storiesData) ? storiesData.map(normalizeIssueAttachments) : [];
          return storiesData;
        }
      } catch (error) {
        //console.error('API Client - Error fetching stories:', error);
        throw error;
      }
    },

    async getTasks(storyId) {
      try {
        
        try {
          const data = await this.fetchHierarchyData({ 
            story_id: storyId,
            type: 'task'
          });
          
          let tasksData = [];
          if (data && data.success && data.data) {
            tasksData = data.data.tasks || data.data || [];
          } else if (data && data.tasks) {
            tasksData = data.tasks;
          } else if (data && Array.isArray(data.data)) {
            tasksData = data.data;
          } else if (Array.isArray(data)) {
            tasksData = data;
          }
          
          // FIXED: Normalize attachments for each task
          tasksData = tasksData.map(normalizeIssueAttachments);
          
          return tasksData;
        } catch (hierarchyError) {
          console.warn('Hierarchy endpoint failed, trying direct task endpoint:', hierarchyError.message);
          const token = localStorage.getItem("token");
          if (!token) {
            alert("No authentication token found. Please log in.");
            return;
          }
    
          const payload = JSON.parse(atob(token.split('.')[1]));
          const company_id = payload.company_id;
          
          const response = await fetch(`${baseURL}/api/tasks?story_id=${storyId}`,{
              headers:{
              Authorization: `Bearer ${token}`,
            }
          });
          if (!response.ok) {
            throw new Error(`Failed to fetch tasks: ${response.status}`);
          }
          
          const data = await response.json();
          let tasksData = data.data || data.tasks || data || [];
          // FIXED: Normalize attachments for each task in fallback
          tasksData = Array.isArray(tasksData) ? tasksData.map(normalizeIssueAttachments) : [];
          return tasksData;
        }
      } catch (error) {
        //console.error('API Client - Error fetching tasks:');
        throw error;
      }
    },

    async getBugs(taskId) {
      try {
        
        try {
          const data = await this.fetchHierarchyData({ 
            task_id: taskId,
            issue_type: '1'
          });
          
          let bugsData = [];
          if (data.success && data.data) {
            bugsData = data.data.bugs || data.data || [];
          } else if (data.bugs) {
            bugsData = data.bugs;
          } else if (Array.isArray(data.data)) {
            bugsData = data.data;
          } else if (Array.isArray(data)) {
            bugsData = data;
          }
          
          // FIXED: Normalize attachments for each bug
          bugsData = bugsData.map(normalizeIssueAttachments);
          
          return bugsData;
        } catch (hierarchyError) {
          console.warn('Hierarchy endpoint failed, trying direct bug endpoint:', hierarchyError.message);
          const token = localStorage.getItem("token");
          if (!token) {
            alert("No authentication token found. Please log in.");
            return;
          }
    
          const payload = JSON.parse(atob(token.split('.')[1]));
          const company_id = payload.company_id;
          
          const response = await fetch(`${baseURL}/api/bugs?task_id=${taskId}`,{
            headers:{
              Authorization: `Bearer ${token}`,
            }
          });
          if (!response.ok) {
            throw new Error(`Failed to fetch bugs: ${response.status}`);
          }
          
          const data = await response.json();
          let bugsData = data.data || data.bugs || data || [];
          // FIXED: Normalize attachments for each bug in fallback
          bugsData = Array.isArray(bugsData) ? bugsData.map(normalizeIssueAttachments) : [];
          return bugsData;
        }
      } catch (error) {
        //console.error('API Client - Error fetching bugs:', error);
        throw error;
      }
    },
    
    async createIssue(issueData, files = []) {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error("No token found");
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
    
        const formData = new FormData();
    
        Object.keys(issueData).forEach(key => {
          const value = issueData[key];
          if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
          }
        });
    
        files.forEach(file => {
          formData.append('files', file);
        });
        
        
        const response = await fetch(`${baseURL}/api/issues`, {
          method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
                      body: formData,

          
        });
    
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
    
        return await response.json();
      } catch (error) {
        //console.error('Error creating issue:', error);
        throw error;
      }
    },
    
    async updateIssue(issueId, issueData, files = []) {
      try {
        const formData = new FormData();
        
        Object.keys(issueData).forEach(key => {
          if (issueData[key] !== null && issueData[key] !== undefined) {
            if (Array.isArray(issueData[key])) {
              formData.append(key, JSON.stringify(issueData[key]));
            } else {
              formData.append(key, issueData[key]);
            }
          }
        });
        
        files.forEach(file => {
          formData.append('files', file);
        });
        
        const token = localStorage.getItem("token");
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
    
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        
        const response = await fetch(`${baseURL}/api/issues/${issueId}`, {
          method: 'PUT',
          
            headers: {
              Authorization: `Bearer ${token}`,
            },
                      body: formData,

          
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        //console.error('Error updating issue:', error);
        throw error;
      }
    },
    
    async getLabels() {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
    
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        const response = await fetch(`${baseURL}/api/labels`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        if (!response.ok) throw new Error('Failed to fetch labels');
        const data = await response.json();
        return data.data || [];
      } catch (error) {
        //console.error('Error fetching labels:', error);
        throw error;
      }
    },
  
    async createLabel(labelData) {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error("No token found");
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
    
        const response = await fetch(`${baseURL}/api/labels`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            name: labelData.name,
            color: labelData.color || '#007bff',
            description: labelData.description || ''
          }),
        });
    
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create label');
        }
    
        return await response.json();
      } catch (error) {
        //console.error('Error creating label:', error);
        throw error;
      }
    },

    // Add to apiClient object in useIssueForm.jsx
async addShaId(issueId, shaData) {
  try {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("No token found");
    
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    
    const response = await fetch(`${baseURL}/api/issues/${issueId}/shas`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(shaData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    //console.error('Error adding SHA ID:', error);
    throw error;
  }
},


async getIssueShaIds(issueId) {
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error("No token found");
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    
    const response = await fetch(`${baseURL}/api/issues/${issueId}/shas`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    //console.error('Error getting issue SHA IDs:', error);
    throw error;
  }
},
    
    async getProjectTeamMembers(projectId) {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
    
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        const response = await fetch(`${baseURL}/api/addteam/project/${projectId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        if (!response.ok) throw new Error('Failed to fetch team members');
        const data = await response.json();
        return data.teamMembers || [];
      } catch (error) {
        //console.error('Error fetching team members:', error);
        throw error;
      }
    },

    async getIssues(filters = {}) {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
    
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        const searchParams = new URLSearchParams();
        
        Object.keys(filters).forEach(key => {
          if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
            searchParams.append(key, filters[key]);
          }
        });
        
        const url = `${baseURL}/api/issues?${searchParams.toString()}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        // FIXED: Normalize attachments for all issues
        if (data && data.data && Array.isArray(data.data)) {
          data.data = data.data.map(normalizeIssueAttachments);
        }
        return data;
      } catch (error) {
        //console.error('Error fetching issues:', error);
        throw error;
      }
    },

    async getIssueById(id) {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
    
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        const response = await fetch(`${baseURL}/api/issues/${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        // FIXED: Normalize attachments for the issue
        if (data && data.data) {
          data.data = normalizeIssueAttachments(data.data);
        } else if (data) {
          // If response is direct issue object
          return normalizeIssueAttachments(data);
        }
        return data;
      } catch (error) {
        //console.error('Error fetching issue:', error);
        throw error;
      }
    },

    async deleteIssue(id) {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
    
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        const response = await fetch(`${baseURL}/api/issues/${id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        //console.error('Error deleting issue:', error);
        throw error;
      }
    }
  };
};

export const useIssueForm = () => {
  const apiClient = createApiClient();
  const location = useLocation();
  const navigate = useNavigate();
  
  const isEditMode = location.state?.editMode || false;
  const issueToEdit = location.state?.issueData || null;

  const [issueData, setIssueData] = useState({
    id: '',
    project_id: '',
    issueType: '',
    summary: '',
    description: '',
    assignee: '',
    reviewer: '', // NEW: Reviewer field (employee ID)
    productOwner: '',
    lead:'',
    productOwnerName: '',
    priority: 3,
    severity: 3,
    status: 1,
    bugStatus: 1,
    version: '', 
    fixedInVersion: '',
    foundInBuild: '',      
    fixedInBuild: '',
    shaIds: [], // Array of SHAs
    newShaId: '',
    labels: [],
    startDate: '',
    endDate: '',
    timeEstimate: '',
    attachments: [],
    epic_id: '',
    story_id: '',
    task_id: '',
    watcher: '' // Single watcher
  });

  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [labels, setLabels] = useState([]);

  const [selectedHierarchy, setSelectedHierarchy] = useState({
    project: null,
    epic: null,
    story: null,
    task: null
  });
  const [projectHierarchy, setProjectHierarchy] = useState(null);
  const [epics, setEpics] = useState([]);
  const [stories, setStories] = useState([]);
  const [tasks, setTasks] = useState([]);

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [hierarchicalFiles, setHierarchicalFiles] = useState({});
  const [groupedFiles, setGroupedFiles] = useState({});

  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [openLabelDialog, setOpenLabelDialog] = useState(false);
  const [showHierarchyDialog, setShowHierarchyDialog] = useState(false);
  const [newLabel, setNewLabel] = useState({
    name: '',
    color: '#0052cc'
  });
  const [labelError, setLabelError] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [isTeamMembersLoading, setIsTeamMembersLoading] = useState(false);
  const [isProductOwnerLoading, setIsProductOwnerLoading] = useState(false);
  const [isLabelLoading, setIsLabelLoading] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [isEpicsLoading, setIsEpicsLoading] = useState(false);
  const [isStoriesLoading, setIsStoriesLoading] = useState(false);
  const [isTasksLoading, setIsTasksLoading] = useState(false);

  const ensureArray = (value) => {
    return Array.isArray(value) ? value : [];
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const getIssueTypeName = (issueTypeId) => {
    const issueTypes = {
      1: 'Bug',
      2: 'Epic',
      3: 'Story', 
      4: 'Task',
      5: 'ChangeRequest'
    };
    return issueTypes[issueTypeId] || 'Unknown';
  };

  const createUploadPath = () => {
    const pathParts = [];
    
    if (selectedHierarchy.project?.name) {
      pathParts.push(selectedHierarchy.project.name);
    }
    
    if (selectedHierarchy.epic?.name) {
      pathParts.push(selectedHierarchy.epic.name);
    }
    
    if (selectedHierarchy.story?.name) {
      pathParts.push(selectedHierarchy.story.name);
    }
    
    if (selectedHierarchy.task?.name) {
      pathParts.push(selectedHierarchy.task.name);
    }
    
    if (issueData.issueType) {
      pathParts.push(getIssueTypeName(parseInt(issueData.issueType)));
    }
    
    return pathParts.length > 0 ? pathParts.join('/') : 'No path selected';
  };

  const fetchProjectHierarchy = async (projectId) => {
    try {
      const hierarchy = await apiClient.getProjectHierarchy(projectId);
      if (hierarchy) {
        setProjectHierarchy(hierarchy);
        setSelectedHierarchy(prev => ({
          ...prev,
          project: hierarchy
        }));
      }
    } catch (error) {
      console.warn('Project hierarchy not available:', error);
    }
  };

  const fetchTeamMembers = async (projectId) => {
    try {
      setIsTeamMembersLoading(true);
      const members = await apiClient.getProjectTeamMembers(projectId);
      setTeamMembers(ensureArray(members));
    } catch (error) {
      //console.error('Error fetching team members:', error);
      setTeamMembers([]);
      showSnackbar('Failed to fetch team members.', 'warning');
    } finally {
      setIsTeamMembersLoading(false);
    }
  };

  const fetchProductOwner = async (projectId) => {
    try {
      setIsProductOwnerLoading(true);
      
      const projectDetails = await apiClient.getProjectDetails(projectId);
      
      if (projectDetails) {
        setIssueData(prev => ({
          ...prev,
          productOwner: projectDetails.product_owner_id || projectDetails.lead_id || '',
          productOwnerName: projectDetails.product_owner_name || projectDetails.lead_name || 'Not assigned'
        }));
      } else {
        setIssueData(prev => ({
          ...prev,
          productOwner: '',
          productOwnerName: 'Project not found'
        }));
      }
    } catch (error) {
      //console.error('Error fetching product owner:', error);
      showSnackbar('Failed to fetch product owner information.', 'warning');
      setIssueData(prev => ({
        ...prev,
        productOwner: '',
        productOwnerName: 'Error loading'
      }));
    } finally {
      setIsProductOwnerLoading(false);
    }
  };

  const fetchEpics = async (projectId) => {
    if (!projectId) {
      setEpics([]);
      return;
    }
    
    setIsEpicsLoading(true);
    
    try {
      const fetchedEpics = await apiClient.getEpics(projectId);
      const safeEpics = ensureArray(fetchedEpics);
      
      setEpics(safeEpics);
      
      if (safeEpics.length === 0) {
      }
    } catch (error) {
      //console.error('Failed to fetch epics:', error);
      setEpics([]);
      showSnackbar(`Failed to load epics: ${error.message}`, 'error');
    } finally {
      setIsEpicsLoading(false);
    }
  };
  
  const fetchStories = async (epicId) => {
    if (!epicId) {
      setStories([]);
      return;
    }
    
    setIsStoriesLoading(true);
    
    try {
      const fetchedStories = await apiClient.getStories(epicId);
      const safeStories = ensureArray(fetchedStories);
      
      setStories(safeStories);
      
      if (safeStories.length === 0) {
      }
    } catch (error) {
      //console.error('Failed to fetch stories:', error);
      setStories([]);
      showSnackbar(`Failed to load stories: ${error.message}`, 'error');
    } finally {
      setIsStoriesLoading(false);
    }
  };
  
  const fetchTasks = async (storyId) => {
    if (!storyId) {
      setTasks([]);
      return;
    }
    
    setIsTasksLoading(true);
    
    try {
      const fetchedTasks = await apiClient.getTasks(storyId);
      const safeTasks = ensureArray(fetchedTasks);
      
      setTasks(safeTasks);
      
      if (safeTasks.length === 0) {
      }
    } catch (error) {
      //console.error('Failed to fetch tasks:', error);
      setTasks([]);
      showSnackbar(`Failed to load tasks: ${error.message}`, 'error');
    } finally {
      setIsTasksLoading(false);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        
        const fetchedProjects = await apiClient.getProjects();
        const safeProjects = ensureArray(fetchedProjects);
        const formattedProjects = safeProjects.map(project => ({
          value: project.id || project.project_id, 
          label: project.name || project.key,
          originalData: project
        }));
        
        setProjects(formattedProjects);
        
        try {
          const fetchedLabels = await apiClient.getLabels();
          const safeLabels = ensureArray(fetchedLabels);
          setLabels(safeLabels);
        } catch (error) {
          console.warn('Failed to fetch labels from API:', error);
          setLabels([]);
        }
      } catch (error) {
        //console.error('Error fetching initial data:', error);
        setProjects([]);
        setLabels([]);
        showSnackbar('Failed to load initial data.', 'warning');
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchInitialData();
  }, []);

// In useIssueForm.jsx - Update the handleChange function

const handleChange = (e) => {
  const { name, value } = e.target;
  
  setIssueData(prev => ({
    ...prev,
    [name]: value
  }));
  
  // If issueType is changing, clear hierarchy errors
  if (name === 'issueType') {
    setErrors(prev => ({
      ...prev,
      epic_id: null,
      story_id: null,
      task_id: null
    }));
  }
  
  if (errors[name]) {
    setErrors(prev => ({
      ...prev,
      [name]: null
    }));
  }
};

  const handleLabelsChange = (event, newValues) => {
    const safeNewValues = ensureArray(newValues);
    setIssueData(prev => ({
      ...prev,
      labels: safeNewValues.map(label => label.name)
    }));
  };

  const handleProjectChange = async (event) => {
    const projectId = event.target.value;
    
    const currentProjects = ensureArray(projects);
    
    if (!Array.isArray(currentProjects)) {
      //console.error('Projects is not an array:', currentProjects);
      showSnackbar('Error: Projects data is not properly loaded', 'error');
      return;
    }
    
    const selectedProject = currentProjects.find(p => p.value === parseInt(projectId));
    
    setIssueData(prev => ({
      ...prev,
      project_id: projectId
    }));
    
    if (selectedProject) {
      const projectData = { 
        id: selectedProject.value, 
        name: selectedProject.label,
        ...selectedProject.originalData
      };
      
      setSelectedHierarchy({
        project: projectData,
        epic: null,
        story: null,
        task: null
      });
      
    } else {
      setSelectedHierarchy({
        project: null,
        epic: null,
        story: null,
        task: null
      });
    }
    
    setEpics([]);
    setStories([]);
    setTasks([]);
    
    if (projectId) {
      
      const fetchPromises = [
        fetchEpics(projectId),
        fetchProductOwner(projectId),
        fetchTeamMembers(projectId),
        fetchProjectHierarchy(projectId)
      ];
      
      const results = await Promise.allSettled(fetchPromises);
      
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const functionNames = ['fetchEpics', 'fetchProductOwner', 'fetchTeamMembers', 'fetchProjectHierarchy'];
          console.warn(`${functionNames[index]} failed:`, result.reason);
        }
      });
    } else {
      setTeamMembers([]);
      setIssueData(prev => ({
        ...prev,
        productOwner: '',
        productOwnerName: '',
        assignee: ''
      }));
    }
    
    if (errors.project_id) {
      setErrors(prev => ({
        ...prev,
        project_id: null
      }));
    }
  };

  const handleEpicChange = async (event) => {
    const epicId = event.target.value;
    
    setIssueData(prev => ({
      ...prev,
      epic_id: epicId,
      story_id: '',
      task_id: ''
    }));
    
    const currentEpics = ensureArray(epics);
    const selectedEpic = currentEpics.find(e => String(e.id || e.epic_id) === String(epicId));
    
    setSelectedHierarchy(prev => ({
      ...prev,
      epic: selectedEpic ? { ...selectedEpic, id: selectedEpic.id || selectedEpic.epic_id } : null,
      story: null,
      task: null
    }));
    
    setStories([]);
    setTasks([]);
    
    if (epicId && epicId !== '') {
      await fetchStories(epicId);
    }
  };
  
  const handleStoryChange = async (event) => {
    const storyId = event.target.value;
    
    setIssueData(prev => ({
      ...prev,
      story_id: storyId,
      task_id: ''
    }));
    
    const currentStories = ensureArray(stories);
    const selectedStory = currentStories.find(s => String(s.id || s.story_id) === String(storyId));
    
    setSelectedHierarchy(prev => ({
      ...prev,
      story: selectedStory ? { ...selectedStory, id: selectedStory.id || selectedStory.story_id } : null,
      task: null
    }));
    
    setTasks([]);
    
    if (storyId && storyId !== '') {
      await fetchTasks(storyId);
    }
  };
  
  const handleTaskChange = (event) => {
    const taskId = event.target.value;
    
    setIssueData(prev => ({
      ...prev,
      task_id: taskId
    }));
    
    const currentTasks = ensureArray(tasks);
    const selectedTask = currentTasks.find(t => String(t.id || t.task_id) === String(taskId));
    
    setSelectedHierarchy(prev => ({
      ...prev,
      task: selectedTask ? { ...selectedTask, id: selectedTask.id || selectedTask.task_id } : null
    }));
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;
    
    setIsFileUploading(true);
    
    try {
      const newFiles = files.map(file => ({
        id: Date.now() + Math.random(),
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadPath: createUploadPath(),
        uploaded: false
      }));
      
      setUploadedFiles(prev => [...prev, ...newFiles]);
      setAttachedFiles(prev => [...prev, ...files]);
      
      const path = createUploadPath();
      setHierarchicalFiles(prev => ({
        ...prev,
        [path]: [...(prev[path] || []), ...newFiles]
      }));
      
      showSnackbar(`${files.length} file(s) attached successfully.`, 'success');
    } catch (error) {
      //console.error('Error handling file upload:', error);
      showSnackbar('Error attaching files.', 'error');
    } finally {
      setIsFileUploading(false);
    }
  };

  const handleFileRemove = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    
    const updatedHierarchicalFiles = { ...hierarchicalFiles };
    Object.keys(updatedHierarchicalFiles).forEach(path => {
      updatedHierarchicalFiles[path] = updatedHierarchicalFiles[path].filter(f => f.id !== fileId);
      if (updatedHierarchicalFiles[path].length === 0) {
        delete updatedHierarchicalFiles[path];
      }
    });
    setHierarchicalFiles(updatedHierarchicalFiles);
    
    showSnackbar('File removed successfully.', 'info');
  };

  const handleCreateLabel = async () => {
    if (!newLabel.name.trim()) {
      setLabelError('Label name is required');
      return;
    }
    
    setIsLabelLoading(true);
    setLabelError('');
    
    try {
      const createdLabel = await apiClient.createLabel(newLabel);
      
      if (createdLabel && createdLabel.data) {
        const updatedLabels = [...labels, createdLabel.data];
        setLabels(updatedLabels);
        
        setIssueData(prev => ({
          ...prev,
          labels: [...prev.labels, createdLabel.data.name]
        }));
        
        setNewLabel({ name: '', color: '#0052cc' });
        setOpenLabelDialog(false);
        showSnackbar('Label created successfully!', 'success');
      }
    } catch (error) {
      //console.error('Error creating label:', error);
      setLabelError(error.message || 'Failed to create label');
    } finally {
      setIsLabelLoading(false);
    }
  };

 // In useIssueForm.jsx - Update the validateForm function

const validateForm = () => {
  const newErrors = {};
  const issueTypeId = parseInt(issueData.issueType);
  
  // Basic validations
  if (!issueData.project_id) {
    newErrors.project_id = 'Project is required';
  }
  
  if (!issueData.issueType) {
    newErrors.issueType = 'Issue type is required';
  }
  
  if (!issueData.summary.trim()) {
    newErrors.summary = 'Summary is required';
  }
  
  if (!issueData.description.trim()) {
    newErrors.description = 'Description is required';
  }
  
  if (!issueData.assignee) {
    newErrors.assignee = 'Assignee is required';
  }
    if (!issueData.lead) {
    newErrors.lead = 'lead is required';
  }
  
  // Hierarchy validations based on issue type
  switch (issueTypeId) {
    case 3: // Story - Requires Epic
      if (!issueData.epic_id) {
        newErrors.epic_id = 'Epic is required for Story type';
      }
      break;
      
    case 4: // Task - Requires Story
      if (!issueData.story_id) {
        newErrors.story_id = 'Story is required for Task type';
      }
      break;
      
    case 1: // Bug - Requires Task
      if (!issueData.task_id) {
        newErrors.task_id = 'Task is required for Bug type';
      }
      break;
      
    // Epic (2) and ChangeRequest (5) don't require hierarchy
    default:
      break;
  }
  
  // Date validations
  if (issueData.startDate && issueData.endDate) {
    const startDate = new Date(issueData.startDate);
    const endDate = new Date(issueData.endDate);
    
    if (startDate > endDate) {
      newErrors.endDate = 'End date must be after start date';
    }
  }
  
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      showSnackbar('Please fix the errors before submitting.', 'error');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Prepare submission data
      const submissionData = {
        ...issueData,
        epic_id: issueData.epic_id || null,
        story_id: issueData.story_id || null, 
        task_id: issueData.task_id || null,
        labels: Array.isArray(issueData.labels) ? issueData.labels.join(',') : issueData.labels,
        priority: parseInt(issueData.priority),
        severity: parseInt(issueData.severity),
        status: parseInt(issueData.status),
        bugStatus: parseInt(issueData.bugStatus),
        project_id: parseInt(issueData.project_id),
        issueType: parseInt(issueData.issueType),
        reviewer: issueData.reviewer || null,
        // Handle SHA IDs - convert from string to array format if needed
        shaIds: issueData.shaIds || [] // NEW: Use shaIds array instead of shaId
      };
      
      // If there's a new SHA ID to add (from separate input field)
      if (issueData.newShaId && issueData.newShaId.trim()) {
        // Add metadata for the new SHA
        submissionData.shaId = issueData.newShaId.trim(); // Send as shaId (single) for backend to process
        submissionData.commitMessage = issueData.commitMessage || "Associated with issue";
        submissionData.author = issueData.author || "System";
        submissionData.timestamp = new Date().toISOString();
      }
      
      // If editing and we have existing SHA IDs, ensure they're included
      if (isEditMode && issueToEdit?.shaIds && Array.isArray(issueToEdit.shaIds)) {
        submissionData.shaIds = issueToEdit.shaIds;
      }
      

      let result;
      if (isEditMode && issueToEdit) {
        // For updates, use the SHA handling logic in updateIssue
        result = await apiClient.updateIssue(issueToEdit.id, submissionData, attachedFiles);
        
        // If we added a new SHA, clear the input field
        if (issueData.newShaId) {
          setIssueData(prev => ({
            ...prev,
            newShaId: '',
            commitMessage: '',
            author: ''
          }));
        }
        
        showSnackbar('Issue updated successfully!', 'success');
        
        // If successful, refresh SHA IDs display
        if (result.success && result.data) {
          try {
            const shaResponse = await apiClient.getIssueShaIds(issueToEdit.id);
            if (shaResponse.success) {
              setIssueData(prev => ({
                ...prev,
                shaIds: shaResponse.data.shas || []
              }));
            }
          } catch (shaError) {
            console.warn('Could not refresh SHA IDs:', shaError);
          }
        }
      } else {
        // For new issues, handle SHA initialization
        if (submissionData.shaId) {
          // Initialize shaIds array with the first SHA
          submissionData.shaIds = [{
            id: submissionData.shaId,
            message: submissionData.commitMessage || "Initial commit",
            author: submissionData.author || "System",
            timestamp: submissionData.timestamp || new Date().toISOString(),
            addedAt: new Date().toISOString()
          }];
          
          // Remove single fields to avoid confusion
          delete submissionData.shaId;
          delete submissionData.commitMessage;
          delete submissionData.author;
          delete submissionData.timestamp;
        } else {
          // Initialize empty array
          submissionData.shaIds = [];
        }
        
        result = await apiClient.createIssue(submissionData, attachedFiles);
        showSnackbar('Issue created successfully!', 'success');
      }
      
      
      // Navigate back on success (optional)
      if (result.success) {
        setTimeout(() => {
          navigate(-1); // Or navigate to issue list
        }, 1500);
      }
      
    } catch (error) {
      //console.error('Error submitting issue:', error);
      showSnackbar(error.message || 'Failed to submit issue.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEditMode && issueToEdit) {
      
      setIssueData({
        id: issueToEdit.id || '',
        project_id: issueToEdit.project_id || '',
        issueType: issueToEdit.issue_type || issueToEdit.issueType || '',
        summary: issueToEdit.summary || '',
        description: issueToEdit.description || '',
        assignee: issueToEdit.assignee_id || issueToEdit.assignee || '',
        reviewer: issueToEdit.reviewer || '', // NEW: Load reviewer
        lead: issueToEdit.lead || '', 
        productOwner: issueToEdit.product_owner_id || issueToEdit.productOwner || '',
        productOwnerName: issueToEdit.product_owner_name || issueToEdit.productOwnerName || '',
        priority: issueToEdit.priority || 3,
        severity: issueToEdit.severity || 3,
        status: issueToEdit.status || 1,
        bugStatus: issueToEdit.bug_status || issueToEdit.bugStatus || 1,
        version: issueToEdit.version || '',
        foundInBuild: issueToEdit.found_in_build || issueToEdit.foundInBuild || '',      // NEW: Add this
        fixedInBuild: issueToEdit.fixed_in_build || issueToEdit.fixedInBuild || '',
        fixedInVersion: issueToEdit.fixed_in_version || issueToEdit.fixedInVersion || '',
        shaIds: issueToEdit.shaIds || issueToEdit.shaId ? [issueToEdit.shaId] : [], // CHANGED: Handle both formats
        newShaId: '', // NEW: Empty for new SHA input
        commitMessage: '', // NEW: Empty for new SHA
        author: '', // NEW: Empty for new SHA
        labels: issueToEdit.labels ? 
          (Array.isArray(issueToEdit.labels) ? issueToEdit.labels : issueToEdit.labels.split(',')) : [],
        startDate: issueToEdit.start_date || issueToEdit.startDate || '',
        endDate: issueToEdit.end_date || issueToEdit.endDate || '',
        timeEstimate: issueToEdit.time_estimate || issueToEdit.timeEstimate || '',
        attachments: ensureAttachmentsArray(issueToEdit.attachments),
        epic_id: issueToEdit.epic_id || '',
        story_id: issueToEdit.story_id || '',
        task_id: issueToEdit.task_id || '',
        watcher: issueToEdit.watcher || '' // Single watcher
      });
      
      if (issueToEdit.project_id) {
        handleProjectChange({ target: { value: issueToEdit.project_id } });
      }
    }
  }, [isEditMode, issueToEdit]);

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  useEffect(() => {
    const grouped = {};
    uploadedFiles.forEach(file => {
      const path = file.uploadPath || 'Ungrouped';
      if (!grouped[path]) {
        grouped[path] = [];
      }
      grouped[path].push(file);
    });
    setGroupedFiles(grouped);
  }, [uploadedFiles, selectedHierarchy]);

  return {
    issueData,
    setIssueData,
    
    projects,
    teamMembers,
    labels,
    epics,
    stories,
    tasks,
    
    selectedHierarchy,
    projectHierarchy,
    
    uploadedFiles,
    attachedFiles,
    hierarchicalFiles,
    groupedFiles,
    
    errors,
    snackbar,
    openLabelDialog,
    setOpenLabelDialog,
    showHierarchyDialog,
    setShowHierarchyDialog,
    newLabel,
    setNewLabel,
    labelError,
    
    isLoading,
    isTeamMembersLoading,
    isProductOwnerLoading,
    isLabelLoading,
    isFileUploading,
    isEpicsLoading,
    isStoriesLoading,
    isTasksLoading,
    
    isEditMode,
    issueToEdit,
    
    handleChange,
    handleLabelsChange,
    handleProjectChange,
    handleEpicChange,
    handleStoryChange,
    handleTaskChange,
    handleFileUpload,
    handleFileRemove,
    handleCreateLabel,
    handleSubmit,
    handleSnackbarClose,
    
    showSnackbar,
    validateForm,
    createUploadPath,
    ensureArray,
    
    apiClient
  };
};